// components/ServicesBanner1.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import HorizontalImageStrip from "../ones/HorizontalImagesStrip";

import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';

import {
  IoMdCheckmarkCircleOutline,
  IoMdCash,
  IoMdDocument,
  IoMdGlobe,
  IoMdArrowDropright,
  IoMdTrendingUp,
  IoMdPeople
} from "react-icons/io";
import { FaShield } from "react-icons/fa6";

export default function ServicesBanner1(props) {

  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  
  // Banner rotation for hero section
  const banners = [banner1, banner2, banner3, banner4, banner1, banner3];

  // ATRIC Core Services - focused on 4 pillars
  const coreServices = [
    {
      id: 1,
      title: "Tax Consultancy",
      icon: <IoMdCash />,
      description: "Comprehensive tax compliance, advisory, and dispute resolution ensuring regulatory adherence while optimizing your tax position across Tanzania and international jurisdictions.",
      features: [
        "Tax Compliance & Filing (VAT, PAYE, WHT, CIT)",
        "Tax Disputes Resolution & Appeals Representation",
        "Transfer Pricing Documentation & International Tax Advisory"
      ],
      gradient: "from-emerald-500/20 to-teal-500/10",
      border: "border-emerald-500/30"
    },
    {
      id: 2,
      title: "Management Accounting",
      icon: <IoMdTrendingUp />,
      description: "Strategic application of management accounting principles to connect strategy with execution, enabling data-driven decisions and sustainable value creation for your organization.",
      features: [
        "Business & Financial Strategy Review",
        "Risk Management & Internal Control Frameworks",
        "Balanced Scorecard Development & KPI Alignment"
      ],
      gradient: "from-blue-500/20 to-indigo-500/10",
      border: "border-blue-500/30"
    },
    {
      id: 3,
      title: "Project Management",
      icon: <IoMdGlobe />,
      description: "Professional oversight of project implementation, monitoring, and evaluation to ensure delivery on time, within budget, and fully aligned with your strategic objectives.",
      features: [
        "End-to-End Project Planning & Execution Oversight",
        "Fund Tracking, M&E Reporting & Donor Compliance",
        "Foreign Direct Investment Facilitation & TIC Support"
      ],
      gradient: "from-violet-500/20 to-purple-500/10",
      border: "border-violet-500/30"
    },
    {
      id: 4,
      title: "Internal Audit",
      icon: <FaShield />,
      description: "Independent assurance services to strengthen governance, risk management, and internal controls—protecting organizational assets and enhancing operational integrity.",
      features: [
        "Internal Controls Design, Review & Testing",
        "Fraud Investigation, Risk Assessments & Compliance Audits",
        "Governance Advisory & Process Improvement Recommendations"
      ],
      gradient: "from-amber-500/20 to-orange-500/10",
      border: "border-amber-500/30"
    }
  ];

  async function init() {
    setReady(false);
    // Simulate async initialization if needed
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!ready) return;
    const interval = setInterval(() => {
      setBgIndex(prev => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [ready]);

  if (!ready) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      {/* ================= HERO SECTION ================= */}
      <section className="MainBodySection position-relative">
        <div style={{
          width: "100%",
          position: "relative",
          overflow: "hidden"
        }}>
          {/* Rotating Banner Backgrounds */}
          {banners.map((banner, i) => (
            <div
              key={i}
              style={{
                backgroundImage: `url(${banner})`,
                backgroundAttachment: "fixed",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                transition: "opacity 2s ease-in-out",
                opacity: i === bgIndex ? 1 : 0,
                zIndex: 0
              }}
              aria-hidden={i !== bgIndex}
            />
          ))}

          {/* Dark Overlay with Content */}
          <div style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 50%, rgba(15,23,42,0.7) 100%)",
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            backdropFilter: "blur(2px)"
          }}>
            <div className="container py-5">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <span className="d-inline-block px-3 py-1 mb-3 rounded-pill" 
                    style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", color: "#10b981", fontSize: "13px", fontWeight: "500" }}>
                    ATRIC Business Consulting Limited
                  </span>
                  
                  <h1 className="text-light mb-3" style={{
                    fontSize: "clamp(2rem, 4vw, 3.2rem)",
                    fontWeight: "700",
                    lineHeight: "1.15",
                    letterSpacing: "-0.5px"
                  }}>
                    Professional Advisory Services for Strategic Growth
                  </h1>

                  <p className="text-light mt-3 mb-4" style={{
                    fontSize: "1.1rem",
                    opacity: 0.9,
                    lineHeight: "1.7",
                    maxWidth: "650px"
                  }}>
                    We provide quality and timely professional services to help CEOs, CFOs, and management teams efficiently oversee organizational performance, ensure regulatory compliance, and manage stakeholder expectations across Tanzania and Africa.
                  </p>

                  <div className="d-flex flex-wrap gap-3">
                    <button
                      className="btn btn-success btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                      onClick={() => appContext.navTo({ item: 'contact_us' })}
                      style={{ 
                        background: "linear-gradient(135deg, #059669, #10b981)",
                        border: "none",
                        fontWeight: "500",
                        boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)"
                      }}
                    >
                      Schedule Consultation
                      <IoMdArrowDropright size={20} />
                    </button>
                    <a 
                      href="https://www.atric.co.tz" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill"
                      style={{ 
                        borderColor: "rgba(255,255,255,0.3)",
                        fontWeight: "500"
                      }}
                    >
                      Visit Website
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= VALUE PROPOSITION ================= */}
      <section style={{
        background: "linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)",
        padding: "70px 0"
      }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-9">
              <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2rem" }}>
                Why Partner with ATRIC?
              </h2>
              <p className="text-light opacity-75 mb-4" style={{ lineHeight: "1.7", fontSize: "1.05rem" }}>
                With highly experienced professional staff and affiliation to ATRIC Consultants (a registered audit firm with 6+ years' experience), we help organizations improve internal controls, enhance decision-making processes, and preserve value through accountability and strategic insight.
              </p>
              
              {/* Core Values Badges */}
              <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                {["Accountability", "Integrity", "Innovation", "Responsiveness", "Transparency", "Competency"].map((value, idx) => (
                  <span key={idx} className="badge rounded-pill px-3 py-2" 
                    style={{ 
                      background: "rgba(255,255,255,0.08)", 
                      border: "1px solid rgba(255,255,255,0.15)",
                      color: "#e2e8f0",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}>
                    {value}
                  </span>
                ))}
              </div>

              <HorizontalImageStrip
                images={[banner1, banner2, banner3, banner4]}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= CORE SERVICES GRID ================= */}
      <section style={{
        background: "#0f172a",
        padding: "80px 0"
      }} id="services">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2.2rem" }}>
              Our Core Service Pillars
            </h2>
            <p className="text-light opacity-75" style={{ maxWidth: "700px", margin: "0 auto", lineHeight: "1.6" }}>
              Focused expertise delivered with professionalism, integrity, and strategic insight to drive your organizational success.
            </p>
          </div>

          <div className="row g-4">
            {coreServices.map((service) => (
              <div className="col-md-6 col-lg-3" key={service.id}>
                <div 
                  className="h-100 p-4 rounded-3 border position-relative overflow-hidden"
                  style={{
                    background: `linear-gradient(145deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))`,
                    backdropFilter: "blur(12px)",
                    borderColor: "rgba(255,255,255,0.08)",
                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 20px 40px -15px rgba(0, 0, 0, 0.4)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Learn more about ${service.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      appContext.navTo({ item: 'contact_us', service: service.title });
                    }
                  }}
                >
                  {/* Decorative gradient accent */}
                  <div className={`position-absolute top-0 end-0 p-3 opacity-20`} 
                    style={{ 
                      background: `linear-gradient(135deg, ${service.gradient.split(' ')[0].replace('/20','/40')}, ${service.gradient.split(' ')[1].replace('/10','/25')})`,
                      width: "80px",
                      height: "80px",
                      borderRadius: "0 0 0 100%",
                      zIndex: 0
                    }} 
                  />

                  {/* Icon Badge */}
                  <div className={`d-inline-flex align-items-center justify-content-center rounded-2 p-2 mb-3`}
                    style={{
                      background: `linear-gradient(135deg, ${service.gradient})`,
                      border: `1px solid rgba(255,255,255,0.15)`,
                      color: "#fff",
                      width: "48px",
                      height: "48px",
                      fontSize: "1.4rem",
                      zIndex: 1
                    }}>
                    {service.icon}
                  </div>

                  {/* Service Content */}
                  <h5 className="text-light mb-2" style={{ fontWeight: "600", fontSize: "1.25rem", zIndex: 1 }}>
                    {service.title}
                  </h5>
                  <p className="text-light opacity-80 mb-3 small" style={{ lineHeight: "1.6", zIndex: 1 }}>
                    {service.description}
                  </p>

                  {/* Feature List */}
                  <ul className="list-unstyled mb-4" style={{ zIndex: 1 }}>
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start gap-2 mb-2 small">
                        <IoMdCheckmarkCircleOutline 
                          className="flex-shrink-0 mt-0.5" 
                          size={16} 
                          style={{ color: "#10b981" }} 
                        />
                        <span className="text-light opacity-85" style={{ fontSize: "0.9rem" }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Link */}
                  <a 
                    href="#contact"
                    className="d-inline-flex align-items-center gap-1 text-decoration-none"
                    style={{ 
                      color: "#60a5fa", 
                      fontWeight: "500", 
                      fontSize: "0.9rem",
                      zIndex: 1,
                      transition: "gap 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.gap = "6px"}
                    onMouseLeave={(e) => e.currentTarget.style.gap = "4px"}
                    onClick={(e) => {
                      e.preventDefault();
                      appContext.navTo({ item: 'contact_us', service: service.title });
                    }}
                  >
                    Explore {service.title}
                    <IoMdArrowDropright size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= METHODOLOGY / APPROACH ================= */}
      <section style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding: "75px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              Our Engagement Approach
            </h3>
            <p className="text-light opacity-75">A structured methodology ensuring quality, relevance, and impact</p>
          </div>

          <div className="row g-4 justify-content-center">
            {[
              { step: "01", title: "Discovery", desc: "In-depth assessment of your objectives, challenges, and operational context" },
              { step: "02", title: "Strategy", desc: "Tailored solutions aligned with your goals, regulatory environment, and risk appetite" },
              { step: "03", title: "Execution", desc: "Professional implementation with clear milestones, communication, and accountability" },
              { step: "04", title: "Review", desc: "Continuous monitoring, reporting, and refinement to sustain long-term value" }
            ].map((item, idx) => (
              <div className="col-md-6 col-lg-3" key={idx}>
                <div className="text-center p-4" style={{ position: "relative" }}>
                  {/* Connector line for desktop */}
                  {idx < 3 && (
                    <div className="d-none d-lg-block position-absolute top-50 end-0" 
                      style={{ 
                        width: "100%", 
                        height: "1px", 
                        background: "linear-gradient(to right, rgba(96,165,250,0.3), transparent)",
                        transform: "translateY(-50%) translateX(50%)",
                        zIndex: 0
                      }} 
                    />
                  )}
                  
                  <div className="position-relative z-1">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto"
                      style={{
                        width: "56px",
                        height: "56px",
                        background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "1.1rem",
                        boxShadow: "0 8px 20px -5px rgba(59, 130, 246, 0.4)"
                      }}>
                      {item.step}
                    </div>
                    <h6 className="text-light mb-2" style={{ fontWeight: "600" }}>{item.title}</h6>
                    <p className="text-light opacity-75 small mb-0" style={{ lineHeight: "1.6" }}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section id="contact" style={{
        background: "linear-gradient(135deg, #0c1424 0%, #1e293b 100%)",
        padding: "65px 0",
        borderTop: "1px solid rgba(255,255,255,0.08)"
      }}>
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <IoMdPeople size={42} className="text-success mb-3" />
              <h3 className="text-light mb-3" style={{ fontWeight: "600" }}>
                Ready to Elevate Your Business Performance?
              </h3>
              <p className="text-light opacity-80 mb-4" style={{ lineHeight: "1.7" }}>
                Connect with our expert team for a confidential consultation. We're committed to delivering best-value professional services aligned with your strategic objectives.
              </p>
              
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <button
                  className="btn btn-success btn-lg px-5 py-2 rounded-pill"
                  onClick={() => appContext.navTo({ item: 'contact_us' })}
                  style={{ 
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    border: "none",
                    fontWeight: "600",
                    boxShadow: "0 6px 20px rgba(16, 185, 129, 0.3)"
                  }}
                >
                  Get in Touch Today
                </button>
                <a 
                  href="tel:+255717918808"
                  className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                  style={{ borderColor: "rgba(255,255,255,0.25)" }}
                >
                  <span style={{ fontSize: "1.1rem" }}>📞</span>
                  +255 717 918 808
                </a>
              </div>
              
              <p className="text-light opacity-60 small mt-4 mb-0">
                📍 2nd Floor, The Tax Hub Building, Industrial Road, Mikocheni B, Dar es Salaam
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}